import { Injectable } from '@angular/core';
import isEqual from 'lodash/isEqual';
import _ from 'lodash';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { Question } from 'app/models/dto/question.model';
import { LocaleSettingService } from 'app/services/i18n/locale-setting.service';
import { isAfter, isBefore } from 'date-fns';

@Injectable({
  providedIn: 'root'
})
export class DealApplicationStatusHelperService {

  constructor(private localeSettingService: LocaleSettingService) { }

  /**
   * re arrange questions in the question list so that items of same group are togeteher
   *
   * @param questionList
   */
  getGroupedFormlyFields(questionList) {
    const groupedlist = _.groupBy(questionList, 'group');
    const groupedItems: any[] = [];
    if (groupedlist) {
      Object.keys(groupedlist).forEach(key => {
        const arr = groupedlist[key];
        groupedItems.push(...arr);
      });
    }
    return groupedItems;
  }

  /**
   * apply classes to formly fields so that they appear one below the other in
   * a 2 column layout
   *
   * @param formlyFields
   */
  getModifiedFieldswithCSSApplied(formlyFields: FormlyFieldConfig[]) {
    const len = formlyFields.length;
    const mid = Math.ceil(len / 2);

    const fieldGroup1 = formlyFields.slice(0, mid);
    const fieldGroup2 = formlyFields.slice(mid, len);

    const group1 = {
      className: 'tw-w-1/2 tw-pr-2',
      fieldGroup: fieldGroup1
    };

    const group2 = {
      className: 'tw-w-1/2 tw-pl-2',
      fieldGroup: fieldGroup2
    };

    const fields: FormlyFieldConfig[]  = [ {
      fieldGroupClassName: 'deal-status-row',
      fieldGroup: [
        group1,
        group2
      ]
    }];

    return fields;
  }

  /**
   * show/hide field names based on the dependency questions
   * add date validations
   *
   * @param formlyFields
   */
  modifyFormlyFields(formlyFields: FormlyFieldConfig[], questionList) {
    formlyFields.forEach(el => {
      // add data-cy attribute for all fields
      if (el.templateOptions) {
        if (el.templateOptions.attributes) {
          el.templateOptions.attributes['data-cy'] = `deal-status-${ el.templateOptions?.attributes?.['data-cy']}`;
        }
      }

      // add the date range validation
      if (el.type === 'date') {
        if (el.templateOptions) {
          el.templateOptions.minDate = new Date(1900, 1, 1);
          el.templateOptions.maxDate = new Date();
        }
        el.validators = {
          minDateValidator: {
            expression:  (viewValue) => {
              if (!viewValue.value) {
                return true;
              }
              if (viewValue.value) {
                const isAfterCheck = isAfter(new Date(Date.now()), new Date('01/01/1900'));
                // if the date entered is not greater than minimum date
                if (!isAfterCheck) {
                  return false;
                }
                return true;
              }
            },
            message: (error: boolean, field: FormlyFieldConfig) =>  'Please enter date greater than 1900'
          },
          maxDateValidator: {
            expression:  (viewValue) => {
              if (!viewValue.value) {
                return true;
              }
              if (viewValue.value) {
                const isBeforeCheck = isBefore(new Date(Date.now()), new Date(viewValue.value));
                // if the date entered is not less than maximum date
                if (!isBeforeCheck) {
                  return false;
                }
                return true;
              }
            },
            message: (error: boolean, field: FormlyFieldConfig) => 'Please enter date less than today'
          }
        };
      }
      // hide fields based on depends on values
      el.hideExpression = (model: any, formState: any, field: FormlyFieldConfig) => {
        const question = questionList.find(qs => field.key === qs.alias);
        return this.showDependency(question, questionList, model);
      };
      // disables fields if group equals 'bankValues'
      if (el.templateOptions) {
        el.templateOptions.disabled = questionList.find(qs => el.key === qs.alias).group === 'bankValues';
      }
      // Prefix zip/postcode label with Borrower/Owner
      if (el.key && ['owner_zip', 'borrower-zipId'].includes(el.key.toString())) {
        const labelPrefix = el.key.toString() === 'owner_zip'
          ? 'Owner'
          : 'Business';
        if (el.templateOptions) {
          el.templateOptions.label = labelPrefix + ' ' + this.localeSettingService.getLocalePostalCodeName();
        }
      }

    });
    return formlyFields;
  }

  getDiffProperties(formlyFields: FormlyFieldConfig[], initModel: object, changedModel: object) {
    return Object.keys(changedModel).reduce((collect: Question[], key) => {
      if (!isEqual(changedModel[key], initModel[key])) {
        let value = changedModel[key];
        if (this.isFieldofTypeMultiSelect(formlyFields, key, value)) {
          value = value.join(',');
        }
        collect.push({ alias: key.replace('-', '.'), value });
      }
      return collect;
    }, []);
  }


  /**
   * converts dots in 'alias' values to hyphen
   *
   * @param borrowerValues
   */
  convertKeyValuestoHypenFormat(borrowerValues: BorrowerValue[]) {
    return borrowerValues
      .map(val => {
        val.alias = val.alias.replace(/\./g, '-');
        return val;
      });
  }

  private isFieldofTypeMultiSelect(formlyFields: FormlyFieldConfig[], key: string, value: string[] | string) {
    const attr = formlyFields.find(el => el.key === key);
    if (attr) {
      return attr.type === 'select' && Array.isArray(value);
    }
  }


  private showDependency(question: BorrowerValue, questionList: any, currModel: object) {
    if (question && question.dependsOn) {
      if (!question.dependsExpression) {
        return false; // show the field if it has no dependsExpression
      } else if (question.dependsExpression) {
        const dependsOnQs = questionList.find(depQ => {
          if (question.dependsOn && depQ.alias === question.dependsOn) {
            return depQ;
          }
          return null;
        });
        if (dependsOnQs) {
          const regex = new RegExp(question.dependsExpression);
          const dependsoNQsVal = currModel?.[dependsOnQs.alias];
          if (regex && !regex.test(dependsoNQsVal)) {
            return true; // hide the field if dependsExpression matches the value of the depends on field
          } else {
            // check the same thing with dependsOnQs, questionList, model
            return this.showDependency(dependsOnQs, questionList, currModel);
          }
        }
      }
    }
    return false; // by default show the field
  }
}
